// Formuläret på byråns webbplats (designen Admin-Widget, ADR 0070): koden att
// klistra in, var formuläret får visas, vad det frågar efter och
// bokningslänken, med en förhandsvisning av första steget.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { ApiFel, data } from '../api/client.js';
import type { components } from '../api/schema.js';
import { useAuth, useMe } from '../auth/auth.js';
import { manadNamn } from '../inkorg/texter.js';
import { Sida } from '../layout/Sida.js';

type Formulardata = components['schemas']['Widget'];
type Val = components['schemas']['WidgetForm'];

/** Adressen dit byrån kan skriva och få hjälp att lägga in formuläret. */
export const HJALPADRESS = 'info@leadledger.se';

type Plattform = 'webb' | 'wordpress' | 'wix';

const PLATTFORMAR: { kod: Plattform; namn: string; text: string }[] = [
  {
    kod: 'webb',
    namn: 'Vanlig webbplats',
    text: 'Ta bort ert gamla kontaktformulär och klistra in den här raden på samma ställe.',
  },
  {
    kod: 'wordpress',
    namn: 'WordPress',
    text: 'Redigera sidan med kontaktformuläret och ta bort det gamla formuläret. Lägg till ett block av typen Anpassad HTML, klistra in raden och uppdatera sidan.',
  },
  {
    kod: 'wix',
    namn: 'Wix och Squarespace',
    text: 'I Wix väljer ni Lägg till, Bädda in kod och Bädda in HTML. I Squarespace lägger ni till ett kodblock. Klistra in raden och ta bort det gamla formuläret.',
  },
];

const VAL: { nyckel: keyof Val; namn: string; hjalp: string }[] = [
  {
    nyckel: 'showQuestion',
    namn: 'Visa "Jag har en fråga"',
    hjalp: 'Behövs om formuläret ersätter kontaktformuläret.',
  },
  {
    nyckel: 'askOrgNumber',
    namn: 'Fråga efter organisationsnummer',
    hjalp: 'Ett valfritt fält i sista steget.',
  },
  {
    nyckel: 'askInvoicesIn',
    namn: 'Fråga om antal leverantörsfakturor',
    hjalp: 'Av: AI uppskattar antalet utifrån verksamheten.',
  },
];

/** Normaliserar som API:t: "https://WWW.byran.se/kontakt" blir "www.byran.se". */
export function domanAv(text: string): string {
  const utanSchema = text
    .trim()
    .toLowerCase()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//, '');
  return (utanSchema.split(/[/?#]/)[0] ?? '').replace(/:\d+$/, '').replace(/\.$/, '');
}

const DOMAN = /^(localhost|(?=.{4,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63})$/;

/** Instruktionerna till byråns webbyrå, som ett mejl med raden. */
function instruktioner(firma: string, snippet: string): string {
  const text = [
    'Hej!',
    '',
    `Vi på ${firma} vill byta vårt kontaktformulär mot ett formulär från LeadLedger. Kan ni ta bort det gamla formuläret och klistra in den här raden på samma ställe?`,
    '',
    snippet,
    '',
    'I WordPress fungerar ett block av typen Anpassad HTML, i Wix Bädda in HTML och i Squarespace ett kodblock.',
    '',
    'Tack!',
  ].join('\n');
  return `mailto:?subject=${encodeURIComponent('Nytt kontaktformulär på vår webbplats')}&body=${encodeURIComponent(text)}`;
}

export function Formular() {
  const me = useMe();
  const { api } = useAuth();
  const queryClient = useQueryClient();
  const kanAndra = me.user.role === 'owner';
  const id = useId();

  const formular = useQuery({
    queryKey: ['formular'],
    queryFn: async () => data(await api.GET('/v1/admin/widgets')).items[0] ?? null,
  });
  const statistik = useQuery({
    queryKey: ['statistik'],
    queryFn: async () => data(await api.GET('/v1/admin/stats')),
  });
  const installningar = useQuery({
    queryKey: ['installningar'],
    queryFn: async () => data(await api.GET('/v1/admin/branding')),
  });

  const [domaner, setDomaner] = useState<string[] | null>(null);
  const [val, setVal] = useState<Val | null>(null);
  const [bokning, setBokning] = useState('');
  const [nyDoman, setNyDoman] = useState('');
  const [domanfel, setDomanfel] = useState<string | null>(null);
  const [plattform, setPlattform] = useState<Plattform>('webb');
  const [kopierad, setKopierad] = useState(false);
  const [kvitto, setKvitto] = useState(false);

  useEffect(() => {
    const f = formular.data;
    if (!f || domaner !== null) return;
    setDomaner(f.domains.map((d) => d.domain));
    setVal(f.form);
    setBokning(f.bookingUrl ?? '');
  }, [formular.data, domaner]);

  const spara = useMutation({
    mutationFn: async (f: Formulardata) =>
      data(
        await api.PUT('/v1/admin/widgets/{id}', {
          params: { path: { id: f.id } },
          body: {
            domains: domaner ?? [],
            form: val ?? f.form,
            bookingUrl: bokning.trim() || null,
          },
        }),
      ),
    onSuccess: (svar) => {
      queryClient.setQueryData(['formular'], svar);
      setKvitto(true);
    },
  });

  if (formular.isError) {
    return (
      <Sida titel="Formulär på er webbplats">
        <div className="meddelande" role="alert">
          Formuläret gick inte att hämta. Ladda om sidan och försök igen.
        </div>
      </Sida>
    );
  }
  if (formular.data === null) {
    return (
      <Sida titel="Formulär på er webbplats">
        <div className="kort tom">
          <p>Byrån har inget formulär än. Kontakta LeadLedger så lägger vi upp det.</p>
        </div>
      </Sida>
    );
  }
  const f = formular.data;
  if (!f || domaner === null || !val) {
    return (
      <Sida titel="Formulär på er webbplats">
        <p role="status">Hämtar formuläret …</p>
      </Sida>
    );
  }

  const hittat = f.domains.some((d) => d.seenAt);
  const s = statistik.data;
  const bokningOk = !bokning.trim() || /^https:\/\/\S+\.\S+/.test(bokning.trim());
  const firma = me.tenant.name;
  const accent = installningar.data?.branding.accentColor ?? '#1E5E46';
  const formularSida = `${f.loaderUrl.replace(/loader\.js$/, '')}?widget=${f.publicId}`;

  function laggTill(e: FormEvent) {
    e.preventDefault();
    const d = domanAv(nyDoman);
    if (!DOMAN.test(d)) {
      setDomanfel('Skriv en domän, till exempel kampanj.byran.se.');
      return;
    }
    setDomanfel(null);
    setKvitto(false);
    if (!domaner?.includes(d)) setDomaner([...(domaner ?? []), d]);
    setNyDoman('');
  }

  const snippet = f.snippet;
  async function kopiera() {
    try {
      await navigator.clipboard.writeText(snippet);
      setKopierad(true);
    } catch {
      setKopierad(false);
    }
  }

  const sparat = (doman: string) => f.domains.find((d) => d.domain === doman);
  const p = PLATTFORMAR.find((x) => x.kod === plattform);

  return (
    <Sida
      titel="Formulär på er webbplats"
      bredvid={
        <span className={`chip ${hittat ? 'chip-gron' : 'chip-gul'}`}>
          {hittat
            ? `Aktivt${s ? ` · ${s.inquiries} ${s.inquiries === 1 ? 'förfrågan' : 'förfrågningar'} i ${manadNamn(s.month)}` : ''}`
            : 'Inte inlagt än'}
        </span>
      }
    >
      <p className="ingress katalog-huvud">
        Formuläret ersätter ert nuvarande kontaktformulär. Det tar emot både offertförfrågningar och
        vanliga frågor.
      </p>
      {!kanAndra && (
        <div className="kort notis" role="note">
          Bara byråns ägare kan ändra formuläret.
        </div>
      )}
      <div className="formularsida">
        <div className="formular-kolumn">
          <section className="kort flikkort" aria-labelledby={`${id}-in`}>
            <h2 id={`${id}-in`} className="avsnitt-rubrik">
              1. Lägg in formuläret
            </h2>
            <div className="segment" role="radiogroup" aria-label="Plattform">
              {PLATTFORMAR.map((x) => (
                <label key={x.kod} className={plattform === x.kod ? 'valt' : undefined}>
                  <input
                    type="radio"
                    name={`${id}-plattform`}
                    checked={plattform === x.kod}
                    onChange={() => setPlattform(x.kod)}
                  />
                  {x.namn}
                </label>
              ))}
            </div>
            <p className="ingress">{p?.text}</p>
            <div className="kodruta">
              <code className="mono">{f.snippet}</code>
              <button type="button" className="btn kopiera" onClick={() => void kopiera()}>
                Kopiera
              </button>
            </div>
            <div className="levande" aria-live="polite">
              {kopierad && <span className="hjalp">Raden är kopierad.</span>}
            </div>
            <div className="knappar">
              <a className="btn btn-s" href={instruktioner(firma, f.snippet)}>
                Skicka instruktioner till er webbyrå
              </a>
              <a
                className="btn btn-g"
                href={`mailto:${HJALPADRESS}?subject=${encodeURIComponent(`Hjälp att lägga in formuläret för ${firma}`)}`}
              >
                Behöver ni hjälp? Vi lägger in det åt er
              </a>
            </div>
          </section>

          <section className="kort flikkort" aria-labelledby={`${id}-dom`}>
            <h2 id={`${id}-dom`} className="avsnitt-rubrik">
              2. Var får formuläret visas?
            </h2>
            {domaner.length === 0 ? (
              <p className="hjalp">
                Formuläret visas på alla webbplatser tills ni lägger till en domän.
              </p>
            ) : (
              <ul className="domaner" aria-label="Domäner">
                {domaner.map((d) => {
                  const sedd = sparat(d)?.seenAt;
                  return (
                    <li key={d}>
                      <span className="mono">{d}</span>
                      <span className={`chip ${sedd ? 'chip-gron' : 'chip-gra'}`}>
                        {sedd
                          ? 'Formuläret hittat'
                          : sparat(d)
                            ? 'Inte hittat än'
                            : 'Ny, inte sparad'}
                      </span>
                      {kanAndra && (
                        <button
                          type="button"
                          className="btn btn-g farlig"
                          aria-label={`Ta bort ${d}`}
                          onClick={() => {
                            setKvitto(false);
                            setDomaner(domaner.filter((x) => x !== d));
                          }}
                        >
                          Ta bort
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            {kanAndra && (
              <form className="nydoman" onSubmit={laggTill} noValidate>
                <label className="visuellt-dold" htmlFor={`${id}-ny`}>
                  Ny domän
                </label>
                <input
                  id={`${id}-ny`}
                  className="in"
                  placeholder="Lägg till domän, till exempel kampanj.byran.se"
                  value={nyDoman}
                  aria-invalid={domanfel ? true : undefined}
                  aria-describedby={domanfel ? `${id}-ny-fel` : undefined}
                  onChange={(e) => setNyDoman(e.target.value)}
                />
                <button type="submit" className="btn btn-s" disabled={!nyDoman.trim()}>
                  Lägg till
                </button>
              </form>
            )}
            {domanfel && (
              <div className="faltfel" id={`${id}-ny-fel`}>
                {domanfel}
              </div>
            )}
          </section>

          <fieldset
            className="kort flikkort panel-falt innehall"
            disabled={!kanAndra || spara.isPending}
          >
            <legend className="avsnitt-rubrik legend-rubrik">3. Innehåll</legend>
            {VAL.map((v) => (
              <label key={v.nyckel} className="vaxel">
                <span>
                  <span className="vaxel-namn">{v.namn}</span>
                  <span className="hjalp">{v.hjalp}</span>
                </span>
                <input
                  type="checkbox"
                  role="switch"
                  checked={val[v.nyckel]}
                  onChange={(e) => {
                    setKvitto(false);
                    setVal({ ...val, [v.nyckel]: e.target.checked });
                  }}
                />
              </label>
            ))}
            <div className="falt">
              <label className="etikett" htmlFor={`${id}-bok`}>
                Länk för att boka uppstartssamtal
              </label>
              <input
                id={`${id}-bok`}
                className="in"
                type="url"
                placeholder="https://"
                value={bokning}
                aria-invalid={bokningOk ? undefined : true}
                aria-describedby={`${id}-bok-hjalp`}
                onChange={(e) => {
                  setKvitto(false);
                  setBokning(e.target.value);
                }}
              />
              <div className={bokningOk ? 'hjalp' : 'faltfel'} id={`${id}-bok-hjalp`}>
                {bokningOk
                  ? 'Kunden ser länken i offerten och i uppföljningsmejlet, och kan boka ett samtal direkt.'
                  : 'Länken ska börja med https://'}
              </div>
            </div>
          </fieldset>

          {spara.isError && (
            <div className="meddelande" role="alert">
              {spara.error instanceof ApiFel && spara.error.status === 400
                ? 'Något stämmer inte. Kontrollera domänerna och bokningslänken.'
                : 'Det gick inte att spara. Försök igen om en stund.'}
            </div>
          )}
          <div className="levande" aria-live="polite">
            {kvitto && <div className="kvitto">Formuläret är sparat.</div>}
          </div>
          {kanAndra && (
            <div className="panel-fot slut">
              <button
                type="button"
                className="btn btn-p"
                disabled={spara.isPending || !bokningOk}
                onClick={() => spara.mutate(f)}
              >
                {spara.isPending ? 'Sparar …' : 'Spara'}
              </button>
            </div>
          )}
        </div>

        <section className="kort flikkort formular-forhand" aria-labelledby={`${id}-forhand`}>
          <div className="resultat-status">
            <h2 id={`${id}-forhand`} className="avsnitt-rubrik">
              Så ser det ut på er sida
            </h2>
            <a className="hogerknapp" href={formularSida} target="_blank" rel="noopener noreferrer">
              Prova formuläret
            </a>
          </div>
          <Forhandsvisning accent={accent} val={val} />
          <p className="hjalp">
            Färg och kontaktuppgifter hämtas från <Link to="/offertmall">Offertmall</Link> och{' '}
            <Link to="/utskick">Utskick</Link>. Det du skickar när du provar hamnar i inkorgen.
          </p>
        </section>
      </div>
    </Sida>
  );
}

/** En bild av formulärets första steg med byråns färg. Den går inte att fylla i. */
function Forhandsvisning({ accent, val }: { accent: string; val: Val }) {
  const steg = val.showQuestion ? 3 : 2;
  return (
    <div className="minibild" style={{ ['--accent' as string]: accent }} aria-hidden="true">
      <div className="minibild-topp">
        <div className="hjalp">Steg 1 av {steg}</div>
        <div className="minibild-steg" style={{ gridTemplateColumns: `repeat(${steg}, 1fr)` }}>
          {Array.from({ length: steg }, (_, i) => (
            <div key={i} className={i === 0 ? 'aktiv' : undefined} />
          ))}
        </div>
      </div>
      <div className="minibild-innehall">
        {val.showQuestion ? (
          <>
            <div className="minibild-rubrik">Vad gäller det?</div>
            <div className="minibild-val valt">
              <span className="minibild-radio" />
              <span>
                <strong>Jag vill ha en offert</strong>
                <span>Få pris på redovisning på några sekunder.</span>
              </span>
            </div>
            <div className="minibild-val">
              <span className="minibild-radio" />
              <span>
                <strong>Jag har en fråga</strong>
                <span>Skicka ett meddelande till oss.</span>
              </span>
            </div>
          </>
        ) : (
          <>
            <div className="minibild-rubrik">Berätta om företaget</div>
            <div className="minibild-falt">Bolagsform</div>
            <div className="minibild-falt">Vad gör företaget?</div>
          </>
        )}
        <div className="minibild-knapp">Fortsätt</div>
      </div>
    </div>
  );
}
